import React, { useEffect, useState } from 'react';
import PageHeaderText from 'components/base/page-header-text';
import UploadXlsBaseMeal from './upload-xls-base-meal';
import {
  BrandOption,
  BrandType,
  CreateBaseMealsXls,
  CreateMealXlsType,
  MealCategoryType
} from 'utils/types';
import { ImageDataType } from 'contexts/new-meal-xls.context/context';
import SingleBottomNav from 'components/base/single-bottom-nav';
import { toast } from 'react-toastify';
import { ToastMessage } from '@cokitchen/cokitchen-components';
import { apiService } from 'services/base.service';
import ReviewBaseMealXlsItem from './review-base-meal-xls-item';
import useDashboardContext from 'contexts/dashboard.context';
import { SuccessModal } from 'components/base/success-modal';
import { checkImageUrlValidity, cleanObject } from 'utils/functions';

const UploadBaseMeal = () => {
  const [meals, setMeals] = useState([] as CreateBaseMealsXls[]);
  const [selectedBrand, setSelectedBrand] = useState({} as BrandOption);
  const [xlsFile, setXlsFile] = useState<File | null>(null);
  const [imageData, setImageData] = useState([] as ImageDataType[]);
  const [imageFiles, setImageFiles] = useState([] as File[]);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [disabled, setDisabled] = useState(true);
  const [formattedMeals, setFormattedMeals] = useState([] as any);
  const [openMeals, setOpenMeals] = useState<(string | undefined)[]>([]);
  const [reviewedMeals, setReviewedMeals] = useState([] as any);
  const [brands, setBrands] = useState([] as BrandType[]);
  const [mealCategories] = useState([] as MealCategoryType[]);
  const { activeCokitchen, category } = useDashboardContext();
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  useEffect(() => {
    fetchBrands();
  }, []);

  const fetchBrands = async () => {
    try {
      const params = {
        cokitchen_id: activeCokitchen.id
      };
      const res = await apiService.get('brand_management/brands', {
        params
      });
      const sortByArchived = res.data
        ?.filter((b: BrandType) => b.item_type === category.toUpperCase() && !b.archived)
        .sort((a: BrandType, b: BrandType) => b.updated_at.localeCompare(a.updated_at));
      setBrands(sortByArchived);
    } catch {
      setBrands([]);
    }
  };

  const isDisabled = () => {
    if (step === 1 && formattedMeals.length === 0) {
      setDisabled(true);
    } else if (step === 2 && reviewedMeals.length !== formattedMeals.length) {
      setDisabled(false);
    } else {
      setDisabled(false);
    }
  };
  useEffect(() => {
    isDisabled();
  }, [reviewedMeals, formattedMeals, selectedBrand]);

  const createMeals = async () => {
    setLoading(true);
    try {
      const payload = formattedMeals.map((meal: any) => {
        return cleanObject({
          amount: meal.amount || '0',
          description: meal.description,
          name: meal.name,
          quantity: meal.quantity,
          images: checkImageUrlValidity(meal.images) || [],
          parent_brand_id: brands.find((brand) => brand.name === meal.parent_brand_id)?.id,
          active: meal.active
        });
      });
      const res: any = await apiService.post('base/bulk-create-meal', {
        data: { meals: payload }
      });
      if (res?.data) {
        toast(<ToastMessage type='success' message={'Meals have been uploaded successfully'} />);
        setShowSuccessModal(true);
      }
    } catch {
      toast(<ToastMessage type='error' message={'Error fetching base meals'} />);
    } finally {
      setLoading(false);
    }
  };
  const toggleAccordion = (idx: string) => {
    const mealExists = openMeals.find((i) => i === idx);
    if (mealExists) {
      setOpenMeals(openMeals?.filter((i) => i !== idx));
    } else {
      setOpenMeals([...openMeals, idx]);
    }
  };
  const handleReviewClick = (meal: Partial<CreateMealXlsType>) => {
    if (!meal.name || meal?.hasError) return;
    setReviewedMeals([...reviewedMeals, meal.name]);
  };
  const updateMeal = (index: number, name: any, e: any) => {
    const meals = [...formattedMeals];
    meals[index][name] = e?.target?.value ?? e;
    setFormattedMeals(meals);
  };

  useEffect(() => {
    setFormattedMeals(
      meals?.map((meal: any) => {
        return {
          amount: String(meal['Updated Price']),
          quantity: meal['Quantity'] ? Number(meal['Quantity']) : '',
          images: [meal['Image']],
          name: meal['Meal'],
          description: meal['Description'] ?? '',
          parent_brand_id: meal['Parent Brand Id'],
          active: false
        };
      })
    );
  }, [meals]);
  return (
    <div>
      <PageHeaderText
        title='Upload base meals via xls'
        description='Create a new meal by uploading necessary files'
      />
      {step === 1 ? (
        <UploadXlsBaseMeal
          imageFiles={imageFiles}
          setImageFiles={setImageFiles}
          imageData={imageData}
          setImageData={setImageData}
          xlsFile={xlsFile}
          setXlsFile={setXlsFile}
          selectedBrand={selectedBrand}
          setSelectedBrand={setSelectedBrand}
          meals={meals}
          setMeals={setMeals}
        />
      ) : (
        formattedMeals?.map((meal: any, i: number) => (
          <div key={i} className='my-4'>
            <ReviewBaseMealXlsItem
              mealIndex={i}
              brands={brands}
              mealCategories={mealCategories}
              isReview={reviewedMeals.includes(meal?.name ?? '')}
              handleReviewClick={handleReviewClick}
              toggleAccordion={() => toggleAccordion(String(i))}
              show={openMeals.includes(String(i))}
              meal={meal as Partial<CreateMealXlsType>}
              updateMeal={updateMeal}
            />
          </div>
        ))
      )}
      <SingleBottomNav
        btnDisabled={disabled}
        onConfirm={() => {
          step === 1 ? setStep(2) : createMeals();
        }}
        loading={loading}
      />
      <SuccessModal
        onClose={() => setShowSuccessModal(false)}
        show={showSuccessModal}
        linkText='PROCEED TO HOME'>
        New base meals has been successfully added
      </SuccessModal>
    </div>
  );
};

export default UploadBaseMeal;
